import { graphlib, layout } from '@dagrejs/dagre';
import { phaseLabel } from './api';
import { excerpt, wrapText, paintLines } from './reviewImageText';

const FONT = 'Nunito, sans-serif';
const CARD_WIDTH = 440;

const diamondIntersection = (node, point) => {
  const dx = point.x-node.x, dy = point.y-node.y;
  const denominator = Math.abs(dx)/(node.width/2) + Math.abs(dy)/(node.height/2);
  return denominator ? { x:node.x+dx/denominator, y:node.y+dy/denominator } : point;
};

const scenarioKey = (condition) => {
  const value = condition.toLowerCase();
  // Keep protocol details attached to their stated viral-load scenario, not to all patients.
  if (value.startsWith('cv ')) {
    const numeric = value.match(/[\d,.]+/)?.[0];
    const comparison = /mayor|>|≥/.test(value) ? 'high' : /menor|<|≤/.test(value) ? 'low' : '';
    if (numeric && comparison) return `${comparison}:${numeric.replace(/,/g,'')}`;
  }
  return value;
};

const cardNode = (ctx, point) => {
  const answer = wrapText(ctx, excerpt(point.answer, 220), CARD_WIDTH - 44, `800 23px ${FONT}`);
  const explanation = point.explanation.trim() === point.answer.trim() ? [] : wrapText(ctx, excerpt(point.explanation, 520), CARD_WIDTH - 44, `400 21px ${FONT}`);
  return { kind: 'card', point, answer, explanation, width: CARD_WIDTH,
    height: 90 + answer.length * 29 + (explanation.length ? 18 + explanation.length * 28 : 0) + 38 };
};

export async function createReviewImage(title, review) {
  await document.fonts.ready;
  const measure = document.createElement('canvas').getContext('2d');
  const graph = new graphlib.Graph().setGraph({ rankdir: 'TB', nodesep: 30, ranksep: 42, marginx: 28, marginy: 24 }).setDefaultEdgeLabel(() => ({}));
  let previous = null;
  review.stages.forEach((stage, index) => {
    const head = `${stage.id}-header`;
    const titleLines = wrapText(measure, `${index+1}. ${phaseLabel(excerpt(stage.title,120).replace(/ /g,'_'))}`, stage.branching ? 380 : 480, `800 26px ${FONT}`);
    const firstScenario = stage.points.findIndex((p) => p.condition);
    const before = stage.branching ? stage.points.slice(0,firstScenario).filter((p) => !p.condition) : [];
    graph.setNode(head, { kind: stage.branching && !before.length ? 'decision' : 'header', width: 580, height: (stage.branching ? 160 : 110) + Math.max(0, titleLines.length-1) * 30, titleLines, stage });
    if (previous) graph.setEdge(previous, head);
    const branched = stage.branching ? stage.points.filter((p) => p.condition) : stage.points;
    const after = stage.branching ? stage.points.slice(firstScenario).filter((p) => !p.condition) : [];
    let origin = head;
    before.forEach((point) => {
      const id = `${stage.id}-${point.id_paso}`;
      graph.setNode(id,cardNode(measure,point)); graph.setEdge(origin,id); origin=id;
    });
    if (before.length) {
      const decision = `${stage.id}-decision`;
      graph.setNode(decision, { kind:'decision', width:580, height:160, titleLines:['Valorar el escenario'], stage });
      graph.setEdge(origin,decision); origin=decision;
    }
    const merge = `${stage.id}-merge`;
    graph.setNode(merge, { kind: 'merge', width: 12, height: 12 });
    const scenarios = new Map();
    branched.forEach((point, pointIndex) => {
      const key = stage.branching ? scenarioKey(point.condition) : String(pointIndex);
      if (!scenarios.has(key)) scenarios.set(key,[]);
      scenarios.get(key).push(point);
    });
    scenarios.forEach((points) => {
      let last = origin;
      points.forEach((point) => {
        const id = `${stage.id}-${point.id_paso}`;
        graph.setNode(id, cardNode(measure, point)); graph.setEdge(last,id); last=id;
      });
      graph.setEdge(last,merge);
    });
    previous = merge;
    after.forEach((point) => {
      const id = `${stage.id}-${point.id_paso}`;
      graph.setNode(id, cardNode(measure, point));
      graph.setEdge(previous, id); previous = id;
    });
  });
  layout(graph);
  const width = Math.max(800, Math.ceil(graph.graph().width));
  const titleLines = wrapText(measure, title, width - 100, `900 38px ${FONT}`);
  const header = 112 + titleLines.length * 46;
  const noteLines = wrapText(measure, review.note, width - 100, `400 19px ${FONT}`);
  const infoLines = wrapText(measure, `${review.included_questions} claves seleccionadas de ${review.total_questions} reactivos. El contenido completo permanece en la masterclass.`, width-100, `600 18px ${FONT}`);
  const footer = 100 + noteLines.length * 25 + infoLines.length * 23;
  const height = Math.ceil(graph.graph().height + header + footer);
  // Keep memory bounded on mobile while retaining a high-resolution, single PNG.
  const scale = Math.min(1, 10000 / height, Math.sqrt(14000000 / (width * height)));
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(width * scale); canvas.height = Math.ceil(height * scale);
  const ctx = canvas.getContext('2d'); ctx.scale(scale, scale);
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, width, height);
  paintLines(ctx, ['PROYECTO RESIDENTE · REPASO ENARM'], 50, 28, `800 19px ${FONT}`, 24);
  paintLines(ctx, titleLines, 50, 67, `900 38px ${FONT}`, 46);
  paintLines(ctx, ['Flujograma de diagnóstico, tratamiento y seguimiento'], 50, header - 35, `400 22px ${FONT}`, 27);
  ctx.save(); ctx.translate((width - graph.graph().width) / 2, header);
  graph.edges().forEach((edge) => {
    const points = graph.edge(edge).points.map((point) => ({ ...point }));
    const from = graph.node(edge.v), to = graph.node(edge.w);
    if (from.kind === 'decision') points[0] = diamondIntersection(from,points[0]);
    if (to.kind === 'decision') points[points.length-1] = diamondIntersection(to,points[points.length-1]);
    ctx.beginPath(); ctx.strokeStyle = '#333'; ctx.lineWidth = 2.5;
    points.forEach((point, i) => i ? ctx.lineTo(point.x, point.y) : ctx.moveTo(point.x, point.y));
    ctx.stroke();
    const end = points[points.length-1], before = points[points.length-2];
    const angle = Math.atan2(end.y-before.y, end.x-before.x);
    ctx.beginPath(); ctx.moveTo(end.x,end.y); ctx.lineTo(end.x-11*Math.cos(angle-.4),end.y-11*Math.sin(angle-.4)); ctx.lineTo(end.x-11*Math.cos(angle+.4),end.y-11*Math.sin(angle+.4)); ctx.closePath(); ctx.fillStyle='#333'; ctx.fill();
  });
  graph.nodes().forEach((id) => {
    const node = graph.node(id), x = node.x - node.width/2, y = node.y - node.height/2;
    if (node.kind === 'merge') { ctx.fillStyle='#111'; ctx.fillRect(x,y,node.width,node.height); return; }
    ctx.lineWidth = 2.5; ctx.strokeStyle = '#111'; ctx.fillStyle = node.kind === 'header' ? '#111' : '#fff';
    if (node.kind === 'decision') {
      ctx.beginPath(); ctx.moveTo(node.x, y); ctx.lineTo(x+node.width,node.y); ctx.lineTo(node.x,y+node.height); ctx.lineTo(x,node.y); ctx.closePath(); ctx.fill(); ctx.stroke();
    } else { ctx.fillRect(x,y,node.width,node.height); ctx.strokeRect(x,y,node.width,node.height); }
    if (node.kind !== 'card') {
      const color = node.kind === 'header' ? '#fff' : '#111';
      paintLines(ctx,node.titleLines,node.x,node.kind === 'decision' ? node.y-node.titleLines.length*15-8 : y+22,`800 26px ${FONT}`,30,color,'center');
      paintLines(ctx,[node.kind === 'decision' ? 'Según el escenario clínico' : 'Claves que se complementan'],node.x,node.kind === 'decision' ? node.y+20 : y+node.height-32,`400 17px ${FONT}`,22,color,'center');
    } else {
      const label = node.point.condition || (node.point.pearl ? 'PERLA DE CONSOLIDACIÓN' : 'CLAVE CLÍNICA');
      const labelLines = wrapText(ctx,label,node.width-44,`800 17px ${FONT}`);
      paintLines(ctx,labelLines,x+22,y+20,`800 17px ${FONT}`,20);
      const answerY = y+38+labelLines.length*20;
      paintLines(ctx,node.answer,x+22,answerY,`800 23px ${FONT}`,29);
      paintLines(ctx,node.explanation,x+22,answerY+node.answer.length*29+18,`400 21px ${FONT}`,28);
      paintLines(ctx,[`Fuente: paso ${node.point.id_paso}`],x+22,y+node.height-28,`600 16px ${FONT}`,20,'#555');
    }
  });
  ctx.restore();
  const footY = header + graph.graph().height + 25;
  paintLines(ctx,infoLines,50,footY,`600 18px ${FONT}`,23);
  paintLines(ctx,noteLines,50,footY+infoLines.length*23+16,`400 19px ${FONT}`,25,'#444');
  const blob = await new Promise((resolve, reject) => canvas.toBlob((value) => value ? resolve(value) : reject(new Error('No se pudo generar la imagen.')), 'image/png'));
  return { blob, width: canvas.width, height: canvas.height };
}